import React, { useRef, useEffect } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Grid, Button, Stack, Fab } from '@mui/material';
import { Box, Container, AppBar, Toolbar, Typography } from '@mui/material';
import { Profile, Education, Experience, Skills, Certifications, Awards } from './components';
import './print.css';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import BuildIcon from '@mui/icons-material/Build';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import Paper from '@mui/material/Paper';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Fade from '@mui/material/Fade';

// Neural Network Dashboard theme
const theme = createTheme({
  typography: {
    fontFamily: '"Inter", "JetBrains Mono", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontSize: '36px',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      color: '#FFFFFF',
      fontFamily: '"Inter", sans-serif',
    },
    h2: {
      fontSize: '32px',
      fontWeight: 600,
      letterSpacing: '-0.02em',
      color: '#FFFFFF',
      fontFamily: '"Inter", sans-serif',
    },
    h3: {
      fontSize: '28px',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      color: '#FFFFFF',
      fontFamily: '"Inter", sans-serif',
    },
    h4: {
      fontSize: '24px',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      color: '#00D4FF',
      fontFamily: '"Inter", sans-serif',
    },
    h5: {
      fontSize: '20px',
      fontWeight: 600,
      color: '#FFFFFF',
      fontFamily: '"Inter", sans-serif',
    },
    h6: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#00D4FF',
      fontFamily: '"Inter", sans-serif',
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: 500,
      color: '#B0BEC5',
      lineHeight: 1.5,
      fontFamily: '"Inter", sans-serif',
    },
    subtitle2: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#B0BEC5',
      lineHeight: 1.4,
      fontFamily: '"Inter", sans-serif',
    },
    body1: {
      fontSize: '16px',
      fontWeight: 400,
      color: '#FFFFFF',
      lineHeight: 1.6,
      fontFamily: '"Inter", sans-serif',
    },
    body2: {
      fontSize: '14px',
      fontWeight: 400,
      color: '#B0BEC5',
      lineHeight: 1.5,
      fontFamily: '"Inter", sans-serif',
    },
    caption: {
      fontSize: '12px',
      fontWeight: 400,
      color: '#78909C',
      lineHeight: 1.4,
      fontFamily: '"JetBrains Mono", monospace',
    },
  },
  palette: {
    primary: {
      main: '#00D4FF', // Electric blue
      light: '#4DE6FF',
      dark: '#0099CC',
      contrastText: '#0B1426',
    },
    secondary: {
      main: '#00FF88', // Neon green
      light: '#4DFFAA',
      dark: '#00CC6A',
      contrastText: '#0B1426',
    },
    background: {
      default: '#0B1426', // Deep navy
      paper: 'rgba(11, 20, 38, 0.95)', // Semi-transparent navy for neural nodes
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B0BEC5',
    },
    grey: {
      50: '#ECEFF1',
      100: '#CFD8DC',
      200: '#B0BEC5',
      300: '#90A4AE',
      400: '#78909C',
      500: '#607D8B',
      600: '#546E7A',
      700: '#455A64',
      800: '#37474F',
      900: '#263238',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'rgba(11, 20, 38, 0.8)',
          borderRadius: '12px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            background: 'rgba(11, 20, 38, 0.9)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 500,
          fontFamily: '"JetBrains Mono", monospace',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'scale(1.02)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #00D4FF 0%, #00FF88 100%)',
          color: '#0B1426',
          boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #00FF88 0%, #00D4FF 100%)',
            boxShadow: '0 0 30px rgba(0, 212, 255, 0.6)',
          },
        },
        outlined: {
          color: '#00D4FF',
          background: 'rgba(0, 212, 255, 0.1)',
          '&:hover': {
            background: 'rgba(0, 212, 255, 0.2)',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          background: 'rgba(11, 20, 38, 0.8)',
          color: '#00D4FF',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'scale(1.05)',
            background: 'rgba(11, 20, 38, 0.9)',
          },
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          background: 'rgba(11, 20, 38, 0.9)',
          borderRadius: '20px 20px 0 0',
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          borderRadius: '12px',
          margin: '4px',
          color: '#B0BEC5',
          '&.Mui-selected': {
            background: 'rgba(0, 212, 255, 0.2)',
            color: '#00D4FF',
            '& .MuiBottomNavigationAction-label': {
              fontWeight: 600,
              fontSize: '0.875rem',
              fontFamily: '"JetBrains Mono", monospace',
            },
            '& .MuiSvgIcon-root': {
              transform: 'scale(1.1)',
              filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.6))',
            },
          },
          '&:hover': {
            background: 'rgba(0, 212, 255, 0.1)',
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(11, 20, 38, 0.9)',
          border: 'none',
          borderBottom: '1px solid rgba(0, 212, 255, 0.3)',
          boxShadow: '0 2px 8px rgba(0, 212, 255, 0.1)',
        },
      },
    },
  },
});

const sectionStyle = {
  background: 'rgba(11, 20, 38, 0.6)',
  borderRadius: '12px',
  padding: { xs: '16px', sm: '32px' },
  marginBottom: { xs: '20px', sm: '32px' },
  position: 'relative',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    background: 'rgba(11, 20, 38, 0.8)',
  },
  '&:not(:last-child)::after': {
    content: '""',
    position: 'absolute',
    bottom: '-16px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, rgba(0, 212, 255, 0.4) 50%, transparent 100%)',
  },
  '@media print': {
    background: 'white !important',
    border: '1px solid #e0e0e0 !important',
    borderRadius: '4px !important',
    padding: '12pt !important',
    marginBottom: '12pt !important',
    position: 'static !important',
    transform: 'none !important',
    '&:hover': {
      transform: 'none !important',
      background: 'white !important',
    },
    '&:not(:last-child)::after': {
      display: 'none !important',
    },
  },
};

const App: React.FC = () => {
  const [value, setValue] = React.useState(0);
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  
  // Define sections with icons
  const sections = [
    { id: 'experience', label: 'Experience', ref: useRef<HTMLDivElement>(null), icon: <WorkIcon /> },
    { id: 'skills', label: 'Skills', ref: useRef<HTMLDivElement>(null), icon: <BuildIcon /> },
    { id: 'education', label: 'Education', ref: useRef<HTMLDivElement>(null), icon: <SchoolIcon /> },
    { id: 'certifications', label: 'Certs', ref: useRef<HTMLDivElement>(null), icon: <CardMembershipIcon /> }
  ];

  // Improved scroll handler with offset
  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    const yOffset = -70; // Adjust this value based on your AppBar height
    const element = ref.current;
    if (element) {
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Add scroll spy functionality
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset + window.innerHeight / 2;
      
      // Find the section that's currently most visible
      let currentSection = 0;
      sections.forEach((section, index) => {
        if (section.ref.current) {
          const element = section.ref.current;
          const { top, bottom } = element.getBoundingClientRect();
          const elementTop = top + window.pageYOffset;
          const elementBottom = bottom + window.pageYOffset;
          
          if (currentScrollPos >= elementTop && currentScrollPos <= elementBottom) {
            currentSection = index;
          }
        }
      });
      
      setValue(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add scroll handler to show/hide button
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowScrollTop(scrollY > 400); // Show button after scrolling 400px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        background: '#0B1426',
        minHeight: '100vh',
        pb: { xs: 7, md: 0 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(0, 212, 255, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(0, 255, 136, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(0, 212, 255, 0.05) 0%, transparent 30%)
          `,
          pointerEvents: 'none',
          zIndex: -1,
        },
        '&::after': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(90deg, transparent 98%, rgba(0, 212, 255, 0.1) 100%),
            linear-gradient(0deg, transparent 98%, rgba(0, 212, 255, 0.1) 100%)
          `,
          backgroundSize: '50px 50px',
          pointerEvents: 'none',
          zIndex: -1,
          opacity: 0.3,
        }
      }}>
        <AppBar
          position="fixed"
          elevation={0}
          className="no-print"
        >
          <Toolbar
            sx={{
              height: '52px',
              minHeight: { xs: '52px' },
              px: { xs: 2, sm: 3 },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {/* Mobile title for xs screens */}
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                display: { xs: 'block', sm: 'none' },
                color: '#FFFFFF',
                fontSize: '16px',
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 600,
              }}
            >
              &gt; asghar_ghorbani.resume()
            </Typography>

            {/* Navigation chain for sm+ screens */}
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
              }}
            >
            <Button
                  variant="text"
                  onClick={() => scrollToTop()}
                  sx={{
                    textTransform: 'none',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: { xs: '12px', md: '13px', lg: '16px'},
                    minWidth: 'auto',
                  }}
                >
                  &gt; asghar_ghorbani.resume()
                </Button>
              {sections.map((section) => (
                <Button
                  key={section.id}
                  variant="outlined"
                  onClick={() => scrollToSection(section.ref)}
                  sx={{
                    textTransform: 'none',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '12px',
                    minWidth: 'auto',
                    px: 1,
                  }}
                >
                  .{section.label.toLowerCase()}()
                </Button>
              ))}
            <Button
              onClick={() => window.print()}
              variant="outlined"
              sx={{
                display: { xs: 'none', sm: 'flex' },
                flexShrink: 0,
              }}
            >
              .export_pdf()
            </Button>
            </Stack>

            {/* Right: Export button */}
          </Toolbar>
        </AppBar>

        <Toolbar className="no-print" /> {/* Spacer for fixed AppBar */}
        <Container
          maxWidth="xl"
          sx={{
            pt: 3,
            pb: 3,
            px: { xs: 1, sm: 4, md: 6 },
          }}
        >
          <Grid
            container
            spacing={3}
            sx={{
              '@media print': {
                display: 'block !important',
                margin: '0 !important',
                width: '100% !important'
              }
            }}
          >
            {/* Profile section - sidebar on lg+, full width on md and below */}
            <Grid item xs={12} lg={3} sx={{
              '@media print': {
                width: '100% !important',
                maxWidth: '100% !important',
                flexBasis: '100% !important',
                order: -1,
                marginBottom: '20pt !important'
              }
            }}>
              <Box
                className="page-break-avoid"
                sx={{
                  position: { lg: 'sticky' },
                  top: { lg: 76 },
                  mb: { xs: 3, lg: 0 },
                  '@media print': {
                    position: 'static !important',
                    marginBottom: '0 !important'
                  }
                }}
              >
                <Profile />
              </Box>
            </Grid>

            {/* Main content area */}
            <Grid item xs={12} lg={9} sx={{
              '@media print': {
                width: '100% !important',
                maxWidth: '100% !important',
                flexBasis: '100% !important',
                order: 1
              }
            }}>
              <Box sx={{
                mt: { xs: 0, lg: 10 },
                '@media print': {
                  marginTop: '0 !important'
                }
              }}>
                {sections.map((section) => (
                  <Grid
                    key={section.id}
                    className="print-section"
                    sx={sectionStyle}
                    ref={section.ref}
                  >
                    {section.id === 'experience' && <Experience />}
                    {section.id === 'skills' && <Skills />}
                    {section.id === 'certifications' && <Certifications />}
                    {section.id === 'education' && <><Education /><Awards /></>}
                  </Grid>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* Add before the BottomNavigation */}
        <Fade in={showScrollTop}>
          <Box
            onClick={scrollToTop}
            role="presentation"
            className="no-print"
            sx={{
              position: 'fixed',
              bottom: { xs: 70, md: 16 }, // Position above bottom navigation on mobile
              right: 16,
              zIndex: 1000,
            }}
          >
            <Fab
              size="small"
              aria-label="scroll back to top"
              className="no-print"
            >
              <KeyboardArrowUpIcon />
            </Fab>
          </Box>
        </Fade>

        {/* Bottom Navigation */}
        <Paper
          className="no-print"
          sx={{
            display: { xs: 'block', sm: 'none' },
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
          }}
          elevation={3}
        >
          <BottomNavigation
            className="no-print"
            value={value}
            onChange={(event, newValue) => {
              setValue(newValue);
              scrollToSection(sections[newValue].ref);
            }}
            showLabels
            sx={{
              height: 56,
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                padding: '6px 8px',
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.625rem',
                  '&.Mui-selected': {
                    fontSize: '0.675rem',
                  },
                },
              },
            }}
          >
            {sections.map((section) => (
              <BottomNavigationAction
                key={section.id}
                label={section.label}
                icon={section.icon}
                sx={{
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'primary.main',
                  },
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default App;